'use client';
import React, { useEffect, useRef, useState } from 'react';

interface TabTransitionProps {
  activeTab: string;
  children: React.ReactNode;
  direction?: 'fade' | 'slide';
}

export function TabTransition({ activeTab, children, direction = 'slide' }: TabTransitionProps) {
  const [displayTab, setDisplayTab] = useState(activeTab);
  const [phase, setPhase] = useState<'visible' | 'exit' | 'enter'>('visible');
  const prevTabRef = useRef(activeTab);

  useEffect(() => {
    if (activeTab === prevTabRef.current) return;
    prevTabRef.current = activeTab;

    setPhase('exit');
    const t1 = setTimeout(() => {
      setDisplayTab(activeTab);
      setPhase('enter');
    }, 160);
    const t2 = setTimeout(() => {
      setPhase('visible');
    }, 320);

    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [activeTab]);

  const getStyle = (): React.CSSProperties => {
    if (direction === 'fade') {
      return {
        opacity: phase === 'exit' ? 0 : phase === 'enter' ? 0 : 1,
        transition: 'opacity 160ms ease',
      };
    }
    return {
      opacity: phase === 'exit' ? 0 : phase === 'enter' ? 0 : 1,
      transform:
        phase === 'exit' ? 'translateY(-6px)'
          : phase === 'enter' ? 'translateY(6px)' : 'translateY(0)',
      transition: 'opacity 160ms ease, transform 160ms ease',
    };
  };

  return (
    <div style={getStyle()} data-tab={displayTab}>
      {children}
    </div>
  );
}

interface TabBarProps {
  tabs: { id: string; label: string; icon?: React.ReactNode; badge?: string | number }[];
  activeTab: string;
  onTabChange: (id: string) => void;
  accentColor?: string;
}

export function TabBar({ tabs, activeTab, onTabChange, accentColor = '#4f7ef8' }: TabBarProps) {
  return (
    <div
      style={{
        display: 'flex',
        gap: '2px',
        backgroundColor: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: '9px',
        padding: '4px',
        flexWrap: 'wrap',
      }}
    >
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '7px 14px',
              borderRadius: '7px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: isActive ? 600 : 400,
              color: isActive ? 'var(--text-primary)' : 'var(--text-muted)',
              backgroundColor: isActive ? 'var(--bg-card)' : 'transparent',
              boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.15)' : 'none',
              transition: 'all 180ms ease',
              whiteSpace: 'nowrap',
            }}
          >
            {tab.icon && <span style={{ color: isActive ? accentColor : 'var(--text-muted)', display: 'flex' }}>{tab.icon}</span>}
            {tab.label}
            {tab.badge !== undefined && (
              <span
                style={{
                  fontSize: '10px',
                  fontWeight: 600,
                  padding: '1px 6px',
                  borderRadius: '20px',
                  backgroundColor: isActive ? accentColor + '18' : 'rgba(100,116,139,0.1)',
                  color: isActive ? accentColor : 'var(--text-muted)',
                }}
              >
                {tab.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
